import React, { useState } from 'react';
import { Blocks as Blockchain, TrendingUp, Activity, HardDrive, Network } from 'lucide-react';

interface P2PNode {
  id: string;
  address: string;
  status: 'online' | 'offline' | 'syncing';
  dataStored: number; // GB
  lastSeen: string;
  location: string;
}

interface BlockchainTransaction {
  hash: string;
  type: 'document_store' | 'access_grant' | 'credential_verify' | 'data_share';
  fromUser: string;
  toUser?: string;
  timestamp: string;
  gasUsed: number;
  status: 'confirmed' | 'pending' | 'failed';
}

export const BlockchainManager: React.FC = () => {
  const [p2pNodes] = useState<P2PNode[]>([
    {
      id: 'QmDemo1PatientNode123',
      address: '0x742d35Cc6609C7b2A1b4c1F4Bb1e8D4C5F6E7D8E',
      status: 'online',
      dataStored: 2.4,
      lastSeen: '2024-01-15T10:30:00Z',
      location: 'Toronto, CA',
    },
    {
      id: 'QmProvider1NodeDEF456',
      address: '0x789c01De2345F6g7H8i9J0k1L2m3N4o5P6q7R8s9',
      status: 'online',
      dataStored: 15.7,
      lastSeen: '2024-01-15T14:20:00Z',
      location: 'New York, US',
    },
    {
      id: 'QmAdminNodeMaster789',
      address: '0xADMIN123456789ABCDEF0123456789ABCDEF0123',
      status: 'syncing',
      dataStored: 45.2,
      lastSeen: '2024-01-15T15:00:00Z',
      location: 'San Francisco, US',
    },
  ]);

  const [recentTransactions] = useState<BlockchainTransaction[]>([
    {
      hash: '0xabc123def456...',
      type: 'document_store',
      fromUser: 'patient-1',
      timestamp: '2024-01-15T14:30:00Z',
      gasUsed: 21000,
      status: 'confirmed',
    },
    {
      hash: '0xdef456ghi789...',
      type: 'access_grant',
      fromUser: 'patient-1',
      toUser: 'provider-1',
      timestamp: '2024-01-15T14:25:00Z',
      gasUsed: 35000,
      status: 'confirmed',
    },
    {
      hash: '0xghi789jkl012...',
      type: 'credential_verify',
      fromUser: 'provider-1',
      timestamp: '2024-01-15T14:20:00Z',
      gasUsed: 18000,
      status: 'pending',
    },
  ]);

  const getNodeStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-800';
      case 'offline': return 'bg-red-100 text-red-800';
      case 'syncing': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'document_store': return 'bg-blue-100 text-blue-800';
      case 'access_grant': return 'bg-green-100 text-green-800';
      case 'credential_verify': return 'bg-purple-100 text-purple-800';
      case 'data_share': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const networkStats = {
    totalNodes: p2pNodes.length,
    onlineNodes: p2pNodes.filter(n => n.status === 'online').length,
    totalDataStored: p2pNodes.reduce((sum, node) => sum + node.dataStored, 0),
    recentTransactions: recentTransactions.length,
  };

  return (
    <div className="space-y-6">
      {/* Blockchain & P2P Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">P2P Nodes</p>
              <p className="text-2xl font-bold text-slate-800">{networkStats.totalNodes}</p>
            </div>
            <Network className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Online Nodes</p>
              <p className="text-2xl font-bold text-slate-800">{networkStats.onlineNodes}</p>
            </div>
            <Activity className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Data Stored</p>
              <p className="text-2xl font-bold text-slate-800">{networkStats.totalDataStored.toFixed(1)}GB</p>
            </div>
            <HardDrive className="h-8 w-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Transactions</p>
              <p className="text-2xl font-bold text-slate-800">{networkStats.recentTransactions}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* P2P Network Status */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">P2P Network Nodes</h3>
              <p className="text-sm text-slate-600">Distributed document storage network</p>
            </div>
            <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
              <Network className="h-4 w-4" />
              <span>Add Node</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Node ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Address</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Data Stored</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Location</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Last Seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {p2pNodes.map((node) => (
                  <tr key={node.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-4">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {node.id.slice(0, 20)}...
                      </code>
                    </td>
                    <td className="py-4 px-4">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {node.address.slice(0, 10)}...{node.address.slice(-6)}
                      </code>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getNodeStatusColor(node.status)}`}>
                        {node.status}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-slate-600">{node.dataStored.toFixed(1)} GB</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-slate-600">{node.location}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-slate-600">
                        {new Date(node.lastSeen).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent Blockchain Transactions */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Recent Blockchain Transactions</h3>
          <p className="text-sm text-slate-600">Smart contract interactions and data operations</p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {recentTransactions.map((tx) => (
              <div key={tx.hash} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                <div className="flex items-center space-x-4">
                  <Blockchain className="h-8 w-8 text-purple-500" />
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTransactionTypeColor(tx.type)}`}>
                        {tx.type.replace('_', ' ')}
                      </span>
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {tx.hash.slice(0, 16)}...
                      </code>
                    </div>
                    <p className="text-sm text-slate-600">
                      From: {tx.fromUser} {tx.toUser && `→ ${tx.toUser}`}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tx.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                      tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {tx.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Gas: {tx.gasUsed.toLocaleString()} • {new Date(tx.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Blockchain Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Network Configuration</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Blockchain Network
              </label>
              <select className="w-full p-3 border border-slate-300 rounded-lg">
                <option value="ethereum">Ethereum Mainnet</option>
                <option value="polygon">Polygon</option>
                <option value="avalanche">Avalanche</option>
                <option value="private">Private Network</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Storage Method
              </label>
              <select className="w-full p-3 border border-slate-300 rounded-lg">
                <option value="ipfs">IPFS + Blockchain</option>
                <option value="arweave">Arweave</option>
                <option value="hybrid">Hybrid (Server + Blockchain)</option>
              </select>
            </div>
            <div className="flex items-center space-x-3">
              <input type="checkbox" id="auto-verify" className="w-4 h-4 text-blue-600" defaultChecked />
              <label htmlFor="auto-verify" className="text-sm text-slate-700">
                Auto-verify provider credentials
              </label>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Smart Contract Features</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">Data Sharing Agreements</p>
                <p className="text-sm text-slate-600">Automated consent management</p>
              </div>
              <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">Provider Verification</p>
                <p className="text-sm text-slate-600">Blockchain credential verification</p>
              </div>
              <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">Research Rewards</p>
                <p className="text-sm text-slate-600">Token rewards for data contribution</p>
              </div>
              <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-slate-300">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
